import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, message, Spin } from 'antd';
import type { Club } from '@/types/models';
import apiService from '@/services/api.service';
import { API_ENDPOINTS } from '@/constants/api.constants';
import { logger } from '@/utils/logger';
import FederationService from '@/services/federation.service';

export interface CreateClubModalProps {
    visible: boolean;
    club: Club | null;
    onCancel: () => void;
    onSuccess: () => void;
}

export const CreateClubModal: React.FC<CreateClubModalProps> = ({
    visible,
    club,
    onCancel,
    onSuccess
}) => {
    // Инициализируем форму с начальными значениями
    const [form] = Form.useForm();
    // Инициализируем состояние для федераций и загрузки
    const [federations, setFederations] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    // Загрузка федераций
    const fetchFederations = async () => {
        try {
            setLoading(true);
            logger.info('[CreateClubModal] Fetching federations for club creation modal...');
            
            // Используем FederationService для единообразия с другими компонентами
            const response = await FederationService.getFederations({
                status: 'active',
                limit: 1000, // Загружаем все федерации
                page: 1,
                sortBy: 'name',
                sortOrder: 'ASC'
            });
            
            logger.info('[CreateClubModal] Federations response received:', {
                hasResponse: !!response,
                hasItems: !!(response && response.items),
                itemsCount: response?.items?.length || 0,
                response: response
            });
            
            if (response && response.items && Array.isArray(response.items)) {
                const federationsData = response.items;
                logger.info(`[CreateClubModal] Loaded ${federationsData.length} active federations:`, federationsData.map(f => ({ id: f.id, name: f.name, status: f.status })));
                setFederations(federationsData);
                
                if (federationsData.length === 0) {
                    logger.warn('[CreateClubModal] No active federations found');
                    message.warning('Не найдено активных федераций. Создайте федерацию перед созданием клуба.');
                }
            } else {
                logger.error('[CreateClubModal] Invalid response structure:', {
                    response,
                    hasResponse: !!response,
                    hasItems: !!(response && response.items),
                    itemsIsArray: Array.isArray(response?.items)
                });
                setFederations([]);
                message.warning('Ошибка при загрузке списка федераций. Проверьте консоль для деталей.');
            }
        } catch (error) {
            logger.error('[CreateClubModal] Error fetching federations:', {
                error,
                errorMessage: error instanceof Error ? error.message : 'Unknown error',
                errorStack: error instanceof Error ? error.stack : undefined
            });
            message.error('Ошибка при загрузке федераций');
            setFederations([]);
        } finally {
            setLoading(false);
        }
    };

    // Загрузка федераций при открытии модального окна
    useEffect(() => {
        if (visible) {
            fetchFederations();
        }
    }, [visible]);

    // Reset form when modal opens/closes or when club changes
    useEffect(() => {
        if (visible) {
            if (club) {
                // Если есть клуб, заполняем форму его данными
                form.setFieldsValue({
                    name: club.name,
                    address: club.address,
                    city: club.city,
                    region: club.region,
                    country: club.country,
                    phone: club.phone,
                    email: club.email,
                    website: club.website,
                    federationId: club.federationId || undefined,
                    // coachName удалено, так как оно не нужно в модели клуба
                    status: club.status || 'active',
                });
            } else {
                // Если клуба нет, сбрасываем форму и устанавливаем значение по умолчанию
                form.resetFields();
                form.setFieldsValue({
                    status: 'active'
                });
            }
        }
    }, [visible, club, form]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            if (club) {
                // Update existing club
                await apiService.put(`${API_ENDPOINTS.CLUBS.BASE}/${club.id}`, values);
                message.success('Клуб успешно обновлен');
            } else {
                // Create new club
                await apiService.post(API_ENDPOINTS.CLUBS.BASE, values);
                message.success('Клуб успешно создан');
            }

            onSuccess();
            form.resetFields();
        } catch (error) {
            logger.error('Error submitting club form:', error);
            message.error('Ошибка при сохранении клуба');
        }
    };

    return (
        <Modal
            title={club ? 'Редактировать клуб' : 'Создать новый клуб'}
            open={visible}
            onCancel={onCancel}
            onOk={handleSubmit}
            okText={club ? 'Сохранить' : 'Создать'}
            cancelText="Отмена"
        >
            <Spin spinning={loading}>
                <Form
                    form={form}
                    layout="vertical"
                >
                <Form.Item
                    name="name"
                    label="Название"
                    rules={[{ required: true, message: 'Введите название клуба' }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="address"
                    label="Адрес"
                    rules={[{ required: true, message: 'Введите адрес' }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="city"
                    label="Город"
                    rules={[{ required: true, message: 'Введите город' }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="region"
                    label="Регион"
                    rules={[{ required: true, message: 'Введите регион' }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="country"
                    label="Страна"
                    rules={[{ required: true, message: 'Введите страну' }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="federationId"
                    label="Федерация"
                >
                    <Select
                        placeholder={loading ? "Загрузка федераций..." : "Выберите федерацию (необязательно)"}
                        allowClear
                        loading={loading}
                        disabled={loading}
                        notFoundContent={loading ? <Spin size="small" /> : "Федерации не найдены"}
                        showSearch
                        filterOption={(input, option) => {
                            const label = typeof option?.children === 'string' 
                                ? option.children 
                                : String(option?.children || '');
                            return label.toLowerCase().includes(input.toLowerCase());
                        }}
                    >
                        {Array.isArray(federations) && federations.length > 0 ? (
                            federations.map(federation => (
                                <Select.Option key={federation.id} value={federation.id}>
                                    {federation.name}
                                </Select.Option>
                            ))
                        ) : !loading ? (
                            <Select.Option value={null} disabled>Федерации не найдены. Создайте федерацию перед созданием клуба.</Select.Option>
                        ) : null}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="phone"
                    label="Телефон"
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="email"
                    label="Email"
                    rules={[{ type: 'email', message: 'Введите корректный email' }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="website"
                    label="Веб-сайт"
                >
                    <Input />
                </Form.Item>

                {/* Второе поле федерации удалено, так как оно дублирует первое */}

                {/* Поле тренера удалено, так как оно не нужно в модели клуба */}

                <Form.Item
                    name="status"
                    label="Статус"
                    rules={[{ required: true, message: 'Выберите статус' }]}
                >
                    <Select>
                        <Select.Option value="active">Активный</Select.Option>
                        <Select.Option value="inactive">Неактивный</Select.Option>
                    </Select>
                </Form.Item>
                </Form>
            </Spin>
        </Modal>
    );
};